import L from "leaflet"
import i18next from "../i18n/i18nInit"
import { bundleDtoToBundle } from "../redux/bosatlas/bosatlas.actions"
import type { Bundle } from "../redux/bosatlas/bosatlas.types"
import type { HttpResponse } from "../redux/http/http.types"
import type { ILatLng } from "../redux/maps/map.types"
import type { Attachment } from "../redux/projects/projects.types"
import type { Project } from "../redux/projects/projects.types"
import { dispatchSnackbar } from "../services/SnackbarService"
import { hasFailed } from "../utils"
import type {
  AttachmentDto,
  CopyProjectCommand,
  CreateProjectCommand,
  LatLng,
  MapDetailDto,
  ProjectDetailDto,
  UpdateProjectCommand
} from "./../models/models"
import type { IMapDrawing, IRectangle } from "./../redux/projects/projects.types"
import { latLngInterfaceToLatLng } from "./AssetService"
import { get, post, put, remove } from "./HttpService"

export async function getProject(projectId: string): Promise<Project> {
  const response: HttpResponse<ProjectDetailDto> = await get<ProjectDetailDto>(
    "project",
    `projects/${projectId}`,
    undefined
  )
  const projectDto: ProjectDetailDto = response.data!

  return projectDetailDtoToProject(projectDto)
}

export async function createProject(project: Project, brandName: string): Promise<string> {
  const drawingsJSON: string = JSON.stringify(
    project.drawings.map((drawing: IMapDrawing) => {
      if ((drawing.shape as any).finished !== undefined) {
        return {
          ...drawing,
          isPopupOpen: false,
          shape: { ...drawing.shape, finished: true }
        }
      }
      return { ...drawing, isPopupOpen: false }
    })
  )

  const command: CreateProjectCommand = {
    metadataId: project.metaDataId,
    title: project.title,
    drawing: drawingsJSON,
    attachments: project.attachments.map(mapAttachmentToAttachmentDto),
    bundleIds: project.bundles.map((bundle: Bundle) => bundle.id!)
  }

  const response: HttpResponse<string> = await post<string, CreateProjectCommand>("project", "projects", command)

  dispatchSnackbar(
    i18next.t("general:project"),
    "POST",
    !hasFailed(response, true),
    i18next.t("project:savedInAtlas", {
      title: project.title,
      brandName
    })
  )
  return response.data!
}

export async function copyProject(project: Project, brandName: string): Promise<string> {
  const drawingsJSON: string = JSON.stringify(
    project.drawings.map((drawing: IMapDrawing) => {
      if ((drawing.shape as any).finished !== undefined) {
        return {
          ...drawing,
          isPopupOpen: false,
          shape: { ...drawing.shape, finished: true }
        }
      }
      return { ...drawing, isPopupOpen: false }
    })
  )

  const command: CopyProjectCommand = {
    projectId: project.id as string,
    title: project.title,
    drawing: drawingsJSON,
    bundleIds: project.bundles.map((bundle: Bundle) => bundle.id!)
  }

  const response: HttpResponse<string> = await post<string, CopyProjectCommand>("project", "projects/copy", command)

  dispatchSnackbar(
    i18next.t("general:project"),
    "POST",
    !hasFailed(response, true),
    i18next.t("project:savedInAtlas", {
      title: project.title,
      brandName
    })
  )

  return response.data!
}

export function createEmptyProjectFromMap(map: MapDetailDto): Project {
  return {
    metaDataId: map.id,
    drawings: [],
    attachments: map.attachments ? map.attachments?.map(attachmentDtoToAttachement) : [],
    bundles: []
  }
}

export function createEmptyProjectFromMapId(mapId: string): Project {
  return {
    metaDataId: mapId,
    drawings: [],
    attachments: [],
    bundles: []
  }
}

export async function updateProject(project: Project): Promise<void> {
  const drawingsJSON: string = JSON.stringify(
    project.drawings.map((drawing: IMapDrawing) => {
      return { ...drawing, isPopupOpen: false }
    })
  )

  const command: UpdateProjectCommand = {
    metadataId: project.metaDataId,
    projectId: project.id as string,
    drawings: drawingsJSON,
    title: project.title,
    attachments: project.attachments.map(mapAttachmentToAttachmentDto),
    bundleIds: project.bundles.map((bundle: Bundle) => bundle.id!)
  }

  const response: HttpResponse = await put<UpdateProjectCommand>("project-save", "projects", command)
  dispatchSnackbar(i18next.t("general:map"), "PUT", !hasFailed(response))
}

export async function removeProject(id: string): Promise<void> {
  const response: HttpResponse = await remove("project", `projects/${id}`)
  dispatchSnackbar("project", "DELETE", !hasFailed(response))
}

export function projectDetailDtoToProject(dto: ProjectDetailDto): Project {
  let drawings: IMapDrawing[] = JSON.parse(dto.drawing ? dto.drawing : "[]") as IMapDrawing[]
  drawings = drawings.map((drawing: IMapDrawing) => {
    if (drawing.shape.type === "rectangle") {
      const shape: IRectangle = drawing.shape as IRectangle
      const corner1: L.LatLng = new L.LatLng(shape.corner1.lat, shape.corner1.lng)
      const corner2: L.LatLng = new L.LatLng(shape.corner2.lat, shape.corner2.lng)
      return {
        ...drawing,
        shape: { ...drawing.shape, corner1, corner2 }
      }
    }

    return drawing
  })

  return {
    id: dto.id,
    title: dto.title as string,
    metaDataId: dto.map?.id as string,
    drawings,
    attachments: dto.attachments ? dto.attachments.map(attachmentDtoToAttachement) : [],
    bundles: dto.bundles ? dto.bundles.map(bundleDtoToBundle) : [],
    profileId: dto.profileId
  }
}

function attachmentDtoToAttachement(dto: AttachmentDto): Attachment {
  return {
    id: dto.id,
    type: dto.type,
    title: dto.title as string,
    description: dto.description as string,
    location: dto.location,
    pointOnMap: dto.pointOnMap ? latLngToLatLngInterface(dto.pointOnMap) : undefined,
    order: dto.order,
    isAuthorContent: dto.isAuthorContent
  }
}

function mapAttachmentToAttachmentDto(attachment: Attachment): AttachmentDto {
  return {
    id: attachment.id,
    type: attachment.type,
    title: attachment.title,
    description: attachment.description,
    location: attachment.location,
    pointOnMap: attachment.pointOnMap ? latLngInterfaceToLatLng(attachment.pointOnMap) : undefined,
    order: attachment.order,
    isAuthorContent: attachment.isAuthorContent
  }
}

function latLngToLatLngInterface(point: LatLng): ILatLng {
  return {
    lat: Number.parseFloat(point.lat as string),
    lng: Number.parseFloat(point.lng as string)
  }
}
